import { useCallback, useEffect, useRef, useState } from 'react';
import {
  searchClientCertifications,
  deleteClientCertification,
  updateClientCertification,
} from '../../services/clientCertificationService';
import { AddClientCertificationModal } from './AddClientCertificationModal';
import type { ClientCertification } from '../../types/clientCertification';

interface Props {
  clientId: string;
  title?: string;
}

/**
 * Ordered list of certifications assigned to a client. Header exposes Add
 * (+) and Refresh. Each row has a drag-handle for reordering and a 3-dot
 * menu with Remove. Drag-drop reorder saves the new `order` value on every
 * affected row via PUT /client-certification/{id}.
 */
export function ClientCertificationList({ clientId, title = 'Certifications' }: Props) {
  const [items, setItems] = useState<ClientCertification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchClientCertifications(
        { clientId },
        { pageSize: 200, orderBy: 'created', order: 'asc', expand: 'certification' },
      );
      // Sort client-side by (order asc, then certification.name for stability)
      results.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return (a.certification?.name ?? '').localeCompare(b.certification?.name ?? '');
      });
      setItems(results);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetch(); }, [fetch]);

  async function handleRemove(id: string) {
    setBusyId(id);
    try {
      await deleteClientCertification(id);
      await fetch();
    } finally {
      setBusyId(null);
    }
  }

  async function commitReorder(next: ClientCertification[]) {
    // Persist new order (1..N) for every row whose order changed.
    const updates: Promise<unknown>[] = [];
    next.forEach((it, idx) => {
      const newOrder = idx + 1;
      if (it.order !== newOrder) {
        updates.push(updateClientCertification(it.id, { order: newOrder }));
      }
    });
    if (updates.length > 0) {
      await Promise.all(updates);
      await fetch();
    }
  }

  function onDragStart(e: React.DragEvent, id: string) {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  }
  function onDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    if (dragId && dragId !== id) setOverId(id);
  }
  function onDragEnd() {
    setDragId(null);
    setOverId(null);
  }
  async function onDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    if (!dragId || dragId === targetId) { onDragEnd(); return; }
    const srcIdx = items.findIndex((i) => i.id === dragId);
    const tgtIdx = items.findIndex((i) => i.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) { onDragEnd(); return; }
    const next = [...items];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    // Optimistic UI, then persist.
    const withNewOrders = next.map((it, idx) => ({ ...it, order: idx + 1 }));
    setItems(withNewOrders);
    onDragEnd();
    await commitReorder(withNewOrders);
  }

  const existingIds = items.map((i) => i.certificationId);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              title="Add"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <button
              type="button"
              onClick={fetch}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6" />
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" />
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">No certifications assigned. Click + to add one.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((it) => (
              <ClientCertificationRow
                key={it.id}
                item={it}
                busy={busyId === it.id}
                dragging={dragId === it.id}
                dragOver={overId === it.id}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
                onRemove={() => handleRemove(it.id)}
              />
            ))}
          </ul>
        )}
      </div>

      {showAdd && (
        <AddClientCertificationModal
          clientId={clientId}
          existingCertificationIds={existingIds}
          onClose={() => setShowAdd(false)}
          onSuccess={async () => { setShowAdd(false); await fetch(); }}
        />
      )}
    </div>
  );
}

interface RowProps {
  item: ClientCertification;
  busy: boolean;
  dragging: boolean;
  dragOver: boolean;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, id: string) => Promise<void>;
  onDragEnd: () => void;
  onRemove: () => void;
}

function ClientCertificationRow({ item, busy, dragging, dragOver, onDragStart, onDragOver, onDrop, onDragEnd, onRemove }: RowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <li
      onDragOver={(e) => onDragOver(e, item.id)}
      onDrop={(e) => onDrop(e, item.id)}
      className={[
        'flex items-center gap-3 px-3 py-2 transition-colors',
        dragging ? 'opacity-50' : '',
        dragOver ? 'bg-blue-50' : '',
        busy ? 'opacity-70 pointer-events-none' : '',
      ].filter(Boolean).join(' ')}
    >
      <span
        draggable
        onDragStart={(e) => onDragStart(e, item.id)}
        onDragEnd={onDragEnd}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 select-none px-1"
        title="Drag to reorder"
        aria-label="Drag handle"
      >
        {/* 6-dot drag glyph */}
        <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
          <circle cx="3" cy="3" r="1.5" /><circle cx="9" cy="3" r="1.5" />
          <circle cx="3" cy="8" r="1.5" /><circle cx="9" cy="8" r="1.5" />
          <circle cx="3" cy="13" r="1.5" /><circle cx="9" cy="13" r="1.5" />
        </svg>
      </span>

      <span className="text-sm font-medium text-gray-700 flex-1 truncate">
        {item.certification?.name ?? item.certificationId}
      </span>

      <span className="text-xs text-gray-400 tabular-nums w-16 text-right">
        Order: {item.order}
      </span>

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
          aria-label="Row menu"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
          </svg>
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
            <button
              onClick={() => { setMenuOpen(false); onRemove(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
